import { useEffect, useState } from 'react';
import Nav from './components/Nav';
import Footer from './components/Footer';
import ContactModal from './components/ContactModal';
import { PrivacyPolicy, TermsOfService } from './components/LegalModals';
import { Router, useRouter } from './hooks/useRouter';
import { useTheme } from './hooks/useTheme';
import Home from './pages/Home';
import Technology from './pages/Technology';
import Civil3d from './pages/Civil3d';
import Capabilities from './pages/Capabilities';
import Company from './pages/Company';
import { COMPANY } from './content/copy';
import './index.css';

function SEO() {
  const { path } = useRouter();
  useEffect(() => {
    const pages = {
      '/': {
        title: `${COMPANY.name} — Physical AI & Spatial Intelligence`,
        description: 'Black Vanilla Technologies builds Physical AI systems that understand the real world. Flagship product: civil3d — spatial intelligence for the built environment.',
      },
      '/technology': {
        title: `Technology — ${COMPANY.name}`,
        description: 'Multimodal perception: LiDAR, vision, IMU, edge compute, cloud reconstruction, and spatial reasoning.',
      },
      '/civil3d': {
        title: 'civil3d — Spatial intelligence for the built environment',
        description: 'civil3d is Black Vanilla’s flagship product: spatial capture, measurable 3D environments, and as-built intelligence.',
      },
      '/capabilities': {
        title: `Capabilities — ${COMPANY.name}`,
        description: 'Spatial intelligence & digital twins, robotic perception & autonomy, industrial vision & edge AI.',
      },
      '/company': {
        title: `Company — ${COMPANY.name}`,
        description: 'Black Vanilla Technologies. Physical AI and Spatial Intelligence. Contact contactus@blackvanilla.ai.',
      },
    };
    const meta = pages[path] || pages['/'];
    document.title = meta.title;
    const set = (name, content, property = false) => {
      const sel = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement('meta');
        if (property) el.setAttribute('property', name);
        else el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    set('description', meta.description);
    set('og:title', meta.title, true);
    set('og:description', meta.description, true);
    set('og:url', `https://blackvanilla.ai${path === '/' ? '' : path}`, true);
    set('og:type', 'website', true);
    set('twitter:card', 'summary_large_image');
    set('twitter:title', meta.title);
    set('twitter:description', meta.description);
  }, [path]);
  return null;
}

function NotFound() {
  const { navigate } = useRouter();
  return (
    <section className="bv-page-hero">
      <div className="bv-wrap">
        <p className="bv-eyebrow">404</p>
        <h1 className="bv-h1">Page not found.</h1>
        <button type="button" className="bv-btn bv-btn-solid" onClick={() => navigate('/')}>Back home</button>
      </div>
    </section>
  );
}

function Shell({ theme, toggleTheme, onContact, onPrivacy, onTerms }) {
  const { path } = useRouter();
  const page = {
    '/': <Home onContact={onContact} />,
    '/technology': <Technology />,
    '/civil3d': <Civil3d onContact={onContact} />,
    '/capabilities': <Capabilities onContact={onContact} />,
    '/company': <Company />,
  }[path] || <NotFound />;

  return (
    <div className="bv-root" data-theme={theme}>
      <SEO />
      <a className="bv-skip" href="#main">Skip to content</a>
      <Nav theme={theme} onToggleTheme={toggleTheme} onContact={onContact} />
      <main id="main">{page}</main>
      <Footer theme={theme} onPrivacy={onPrivacy} onTerms={onTerms} onContact={onContact} />
    </div>
  );
}

export default function App() {
  const { theme, toggleTheme } = useTheme();
  const [showContact, setShowContact] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  return (
    <Router>
      <Shell
        theme={theme}
        toggleTheme={toggleTheme}
        onContact={() => setShowContact(true)}
        onPrivacy={() => setShowPrivacy(true)}
        onTerms={() => setShowTerms(true)}
      />
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
    </Router>
  );
}
